import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BookOpen, Calendar, Users, Check, Sparkles, Loader2, CheckCircle, AlertCircle, CreditCard, XCircle, Search, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import axios from 'axios';
import './CoursesEvents.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Event poster images
const eventPosters = [
  {
    id: 1,
    src: "https://customer-assets.emergentagent.com/job_aec52aac-54e5-4016-8334-de13af7543de/artifacts/hd3tslvr_WhatsApp%20Image%202026-03-05%20at%2013.37.49.jpeg",
    alt: "Firstborn Daughters Convention - Tshidi Tsimile Panel Speaker"
  },
  {
    id: 2,
    src: "https://customer-assets.emergentagent.com/job_aec52aac-54e5-4016-8334-de13af7543de/artifacts/uktmtjkp_WhatsApp%20Image%202026-03-09%20at%2007.23.05.jpeg",
    alt: "Firstborn Daughters Convention - All Speakers"
  }
];

export const CoursesEvents = () => {
  const [searchParams] = useSearchParams();
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState(null); // 'paystack' or 'payfast'
  const [currentPoster, setCurrentPoster] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: ''
  });
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const errorRef = useRef(null);
  
  // Subscription management states
  const [showManageSubscription, setShowManageSubscription] = useState(false);
  const [manageEmail, setManageEmail] = useState('');
  const [subscription, setSubscription] = useState(null);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState('');
  const [subscriptionError, setSubscriptionError] = useState('');
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookingData, setBookingData] = useState({ email: '', firstName: '', lastName: '', phoneNumber: '' });
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState('');

  // Scroll to error message when it appears
  useEffect(() => {
    if (errorMessage && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [errorMessage]);

  // Check if returning from successful payment
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const reference = searchParams.get('reference') || searchParams.get('trxref');
    const gateway = searchParams.get('gateway');
    
    if (paymentStatus === 'success') {
      if (gateway === 'payfast') {
        // PayFast returns directly, ITN handles verification
        setSuccessMessage('🎉 Welcome to BASADING WOMB & RELATIONSHIPS ARCADEMY™! Your subscription is being activated.');
        window.history.replaceState({}, document.title, window.location.pathname);
      } else if (reference) {
        verifyPayment(reference);
      }
    }
  }, [searchParams]);

  // Auto-slide effect for event posters
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPoster((prev) => (prev + 1) % eventPosters.length);
    }, 5000); // Change slide every 5 seconds
    
    return () => clearInterval(interval);
  }, []);

  const nextPoster = () => {
    setCurrentPoster((prev) => (prev + 1) % eventPosters.length);
  };

  const prevPoster = () => {
    setCurrentPoster((prev) => (prev - 1 + eventPosters.length) % eventPosters.length);
  };

  const openLightbox = (poster) => {
    setLightboxImage(poster);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage(null);
  };

  // Check subscription status
  const checkSubscription = async () => {
    if (!manageEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(manageEmail)) {
      setSubscriptionError('Please enter a valid email address');
      return;
    }
    
    setIsCheckingSubscription(true);
    setSubscriptionError('');
    setSubscriptionMessage('');
    setSubscription(null);
    
    try {
      const response = await axios.get(`${API_URL}/api/subscriptions/user/${encodeURIComponent(manageEmail)}`);
      if (response.data.active && response.data.subscription) {
        setSubscription(response.data.subscription);
      } else {
        setSubscriptionError('No active subscription found for this email address.');
      }
    } catch (error) {
      setSubscriptionError('Failed to check subscription. Please try again.');
    } finally {
      setIsCheckingSubscription(false);
    }
  };

  // Cancel subscription
  const cancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your BASADING WOMB & RELATIONSHIPS ARCADEMY™ subscription? You will lose access to all membership benefits.')) {
      return;
    }
    
    setIsCancelling(true);
    setSubscriptionError('');
    
    try {
      await axios.post(`${API_URL}/api/subscriptions/cancel?email=${encodeURIComponent(manageEmail)}`);
      setSubscription(null);
      setSubscriptionMessage('Your subscription has been cancelled successfully. We\'re sad to see you go! 💜');
      setManageEmail('');
    } catch (error) {
      setSubscriptionError('Failed to cancel subscription. Please contact support.');
    } finally {
      setIsCancelling(false);
    }
  };

  const subscriptionBenefits = [
    'Access to exclusive healing content and resources',
    'Monthly live healing spaces and ceremonies',
    'Private community access for support and connection',
    'Discounted rates on workshops and events',
    'Monthly guided meditations and practices',
    'Priority booking for in-person sessions'
  ];

  const upcomingCourses = [
    {
      title: 'Womb Healing & Feminine Embodiment',
      description: 'A transformative journey into sacred feminine healing and embodiment practices.',
      platform: 'Coming Soon',
      status: 'In Development'
    },
    {
      title: 'Sacred Union Masterclass',
      description: 'Integration of inner masculine & feminine energies for wholeness and balance.',
      platform: 'Coming Soon',
      status: 'In Development'
    },
    {
      title: 'Embodied Healing Practitioner Training',
      description: 'Comprehensive training for those called to guide others in embodiment work.',
      platform: 'Coming Soon',
      status: 'In Development'
    }
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.firstName || formData.firstName.length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    }
    if (!formData.lastName || formData.lastName.length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const verifyPayment = async (reference) => {
    try {
      setIsSubscribing(true);
      const storedData = localStorage.getItem('womanity_subscription_data');
      let userData = { email: '', firstName: '', lastName: '', phoneNumber: '' };
      
      if (storedData) {
        userData = JSON.parse(storedData);
      }

      const response = await axios.post(`${API_URL}/api/subscriptions/verify`, {
        reference: reference,
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        phoneNumber: userData.phoneNumber
      });

      if (response.data.success) {
        setSuccessMessage('🎉 Welcome to BASADING WOMB & RELATIONSHIPS ARCADEMY™! Your subscription is now active.');
        localStorage.removeItem('womanity_subscription_data');
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    } catch (error) {
      console.error('Verification error:', error);
      setErrorMessage('Payment verification failed. Please contact support if you were charged.');
    } finally {
      setIsSubscribing(false);
    }
  };

  const handlePaystackPayment = async () => {
    if (!validateForm()) return;
    
    setIsSubscribing(true);
    setErrorMessage('');

    try {
      localStorage.setItem('womanity_subscription_data', JSON.stringify(formData));

      const response = await axios.post(`${API_URL}/api/subscriptions/initialize`, formData);

      if (response.data.success && response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      } else {
        setErrorMessage('Failed to initialize payment. Please try again.');
        setIsSubscribing(false);
      }
    } catch (error) {
      console.error('Subscription error:', error);
      const detail = error.response?.data?.detail || '';
      const status = error.response?.status;
      let message = '';
      
      if (detail.includes('active subscription') || detail.includes('active BASADING')) {
        message = 'You already have an active BASADING WOMB & RELATIONSHIPS ARCADEMY™ subscription with this email. Check your subscription status below.';
      } else if (detail.includes('subscription plan')) {
        message = 'Unable to set up the subscription plan. This is a temporary issue — please try again in a moment.';
      } else if (detail.includes('Payment initialization failed')) {
        message = 'Payment gateway could not be reached. Please check your internet connection and try again.';
      } else if (status === 500) {
        message = `Server error: ${detail || 'An unexpected error occurred. Please try again or contact support.'}`;
      } else if (detail) {
        message = detail;
      } else if (error.message && error.message.includes('Network')) {
        message = 'Network error: Please check your internet connection and try again.';
      } else {
        message = 'Something went wrong while processing your subscription. Please try again or contact support.';
      }
      
      setErrorMessage(message);
      setIsSubscribing(false);
    }
  };

  const handlePayFastPayment = async () => {
    if (!validateForm()) return;
    
    setIsSubscribing(true);
    setErrorMessage('');

    try {
      localStorage.setItem('womanity_subscription_data', JSON.stringify(formData));

      const response = await axios.post(`${API_URL}/api/subscriptions/payfast/initialize`, formData);

      if (response.data.success && response.data.checkout_url) {
        // Create a hidden form and submit to PayFast
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = response.data.checkout_url;

        Object.entries(response.data.payment_data).forEach(([key, value]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = value;
          form.appendChild(input);
        });

        document.body.appendChild(form);
        form.submit();
      } else {
        setErrorMessage('Failed to initialize PayFast payment. Please try again.');
        setIsSubscribing(false);
      }
    } catch (error) {
      console.error('PayFast error:', error);
      const message = error.response?.data?.detail || 'Failed to initialize PayFast payment. Please try again.';
      setErrorMessage(message);
      setIsSubscribing(false);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    
    if (selectedGateway === 'paystack') {
      await handlePaystackPayment();
    } else if (selectedGateway === 'payfast') {
      await handlePayFastPayment();
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className="courses-events-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <h1 className="heading-hero">Courses & Events</h1>
          <p className="body-large" style={{ maxWidth: '700px', margin: '1.5rem auto 0' }}>
            Deepen your healing journey through courses, workshops, and community connection
          </p>
        </div>
      </section>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="container" style={{ marginTop: '2rem' }}>
          <div className="success-alert" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '1rem 1.5rem',
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(16, 185, 129, 0.05))',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: '0.75rem',
            color: '#047857'
          }}>
            <CheckCircle size={24} />
            <span className="body-medium">{successMessage}</span>
          </div>
        </div>
      )}

      {errorMessage && (
        <div ref={errorRef} className="container" style={{ marginTop: '2rem' }}>
          <div className="error-alert" data-testid="subscription-error" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '1rem 1.5rem',
            background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(239, 68, 68, 0.05))',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '0.75rem',
            color: '#dc2626'
          }}>
            <AlertCircle size={24} />
            <span className="body-medium">{errorMessage}</span>
          </div>
        </div>
      )}

      {/* BASADING Subscription Section */}
      <section className="womanity-section">
        <div className="container">
          <div className="womanity-content">
            <div className="womanity-header">
              <Sparkles size={48} className="womanity-icon" />
              <h2 className="heading-1" style={{ marginBottom: '1rem' }}>BASADING WOMB & RELATIONSHIPS ARCADEMY™ MEMBERSHIP</h2>
              <p className="body-large" style={{ marginBottom: '2rem', color: 'var(--text-secondary)' }}>
                Join our sacred community for ongoing healing, connection, and transformation
              </p>
            </div>

            <div className="subscription-grid">
              <Card className="subscription-card featured-card">
                <CardHeader className="subscription-card-header">
                  <CardTitle>Monthly Membership</CardTitle>
                  <div className="subscription-price">
                    <span className="price-currency">R</span>
                    <span className="price-amount">333</span>
                    <span className="price-period">/month</span>
                  </div>
                  <CardDescription>Cancel anytime, no long-term commitment</CardDescription>
                </CardHeader>
                <CardContent className="subscription-card-content">
                  <div className="benefits-list">
                    {subscriptionBenefits.map((benefit, index) => (
                      <div key={index} className="benefit-item">
                        <Check size={20} className="benefit-check" />
                        <span className="body-medium">{benefit}</span>
                      </div>
                    ))}
                  </div>

                  {!showForm ? (
                    <Button 
                      className="subscribe-button btn-primary"
                      onClick={() => { setShowForm(true); setSelectedGateway('paystack'); }}
                      disabled={isSubscribing || successMessage}
                    >
                      {successMessage ? 'Subscribed ✓' : 'Subscribe Now'}
                    </Button>
                  ) : (
                    /* Subscription Form */
                    <form onSubmit={handleSubscribe} className="subscription-form" style={{ marginTop: '1.5rem' }}>

                      <div className="form-group" style={{ marginBottom: '1rem' }}>
                        <label className="body-small" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                          Email Address *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="you@example.com"
                          className={`form-input ${errors.email ? 'error' : ''}`}
                          style={{
                            width: '100%',
                            padding: '0.75rem 1rem',
                            borderRadius: '0.5rem',
                            border: errors.email ? '2px solid #ef4444' : '1px solid var(--border-color)',
                            fontSize: '1rem',
                            background: 'var(--bg-page)'
                          }}
                        />
                        {errors.email && <span className="error-text" style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{errors.email}</span>}
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                        <div className="form-group">
                          <label className="body-small" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                            First Name *
                          </label>
                          <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            placeholder="Jane"
                            className={`form-input ${errors.firstName ? 'error' : ''}`}
                            style={{
                              width: '100%',
                              padding: '0.75rem 1rem',
                              borderRadius: '0.5rem',
                              border: errors.firstName ? '2px solid #ef4444' : '1px solid var(--border-color)',
                              fontSize: '1rem',
                              background: 'var(--bg-page)'
                            }}
                          />
                          {errors.firstName && <span className="error-text" style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{errors.firstName}</span>}
                        </div>
                        <div className="form-group">
                          <label className="body-small" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                            Last Name *
                          </label>
                          <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            placeholder="Doe"
                            className={`form-input ${errors.lastName ? 'error' : ''}`}
                            style={{
                              width: '100%',
                              padding: '0.75rem 1rem',
                              borderRadius: '0.5rem',
                              border: errors.lastName ? '2px solid #ef4444' : '1px solid var(--border-color)',
                              fontSize: '1rem',
                              background: 'var(--bg-page)'
                            }}
                          />
                          {errors.lastName && <span className="error-text" style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>{errors.lastName}</span>}
                        </div>
                      </div>

                      <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                        <label className="body-small" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                          Phone Number (Optional)
                        </label>
                        <input
                          type="tel"
                          name="phoneNumber"
                          value={formData.phoneNumber}
                          onChange={handleInputChange}
                          placeholder="+27 60 000 0000"
                          className="form-input"
                          style={{
                            width: '100%',
                            padding: '0.75rem 1rem',
                            borderRadius: '0.5rem',
                            border: '1px solid var(--border-color)',
                            fontSize: '1rem',
                            background: 'var(--bg-page)'
                          }}
                        />
                      </div>

                      <div style={{ display: 'flex', gap: '1rem' }}>
                        <Button 
                          type="button"
                          className="btn-secondary"
                          onClick={() => {
                            setShowForm(false);
                            setSelectedGateway(null);
                          }}
                          style={{ flex: 1 }}
                        >
                          Cancel
                        </Button>
                        <Button 
                          type="submit"
                          className="subscribe-button btn-primary"
                          disabled={isSubscribing}
                          style={{ flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                        >
                          {isSubscribing ? (
                            <>
                              <Loader2 size={20} className="animate-spin" />
                              Processing...
                            </>
                          ) : (
                            'Pay R333 — Subscribe Now'
                          )}
                        </Button>
                      </div>
                    </form>
                  )}

                  <p className="body-small" style={{ textAlign: 'center', marginTop: '1rem', color: 'var(--text-muted)' }}>
                    Secure payment via Paystack
                  </p>

                  {/* Manage Subscription Link */}
                  <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--border-color)' }}>
                    <button
                      data-testid="manage-subscription-toggle"
                      onClick={() => {
                        setShowManageSubscription(!showManageSubscription);
                        setSubscription(null);
                        setSubscriptionError('');
                        setSubscriptionMessage('');
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--text-muted)',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        textDecoration: 'underline',
                        width: '100%',
                        textAlign: 'center'
                      }}
                    >
                      {showManageSubscription ? 'Hide subscription management' : 'Already a member? Manage your subscription'}
                    </button>

                    {showManageSubscription && (
                      <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--bg-page)', borderRadius: '0.75rem' }}>
                        {subscriptionMessage && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.75rem',
                            marginBottom: '1rem',
                            background: 'rgba(16, 185, 129, 0.1)',
                            border: '1px solid rgba(16, 185, 129, 0.3)',
                            borderRadius: '0.5rem',
                            color: '#047857',
                            fontSize: '0.875rem'
                          }} data-testid="manage-subscription-message">
                            <CheckCircle size={18} />
                            {subscriptionMessage}
                          </div>
                        )}

                        {subscriptionError && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.75rem',
                            marginBottom: '1rem',
                            background: 'rgba(239, 68, 68, 0.1)',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            borderRadius: '0.5rem',
                            color: '#dc2626',
                            fontSize: '0.875rem'
                          }} data-testid="manage-subscription-error">
                            <AlertCircle size={18} />
                            {subscriptionError}
                          </div>
                        )}

                        {!subscription ? (
                          <div>
                            <label className="body-small" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                              Enter your email to check subscription
                            </label>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <input
                                type="email"
                                value={manageEmail}
                                onChange={(e) => setManageEmail(e.target.value)}
                                placeholder="your@email.com"
                                data-testid="manage-email-input"
                                style={{
                                  flex: 1,
                                  padding: '0.75rem 1rem',
                                  borderRadius: '0.5rem',
                                  border: '1px solid var(--border-color)',
                                  fontSize: '0.875rem'
                                }}
                                onKeyDown={(e) => e.key === 'Enter' && checkSubscription()}
                              />
                              <Button
                                onClick={checkSubscription}
                                disabled={isCheckingSubscription}
                                data-testid="check-subscription-btn"
                                className="btn-secondary"
                                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                              >
                                {isCheckingSubscription ? (
                                  <Loader2 size={16} className="animate-spin" />
                                ) : (
                                  <Search size={16} />
                                )}
                                Check
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div style={{
                              padding: '1rem',
                              background: 'rgba(139, 92, 246, 0.1)',
                              borderRadius: '0.5rem',
                              marginBottom: '1rem'
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                <span className="body-small" style={{ fontWeight: '600' }}>BASADING WOMB & RELATIONSHIPS ARCADEMY™</span>
                                <span style={{
                                  padding: '0.25rem 0.75rem',
                                  background: subscription.status === 'active' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                                  color: subscription.status === 'active' ? '#047857' : '#dc2626',
                                  borderRadius: '1rem',
                                  fontSize: '0.75rem',
                                  fontWeight: '600',
                                  textTransform: 'uppercase'
                                }} data-testid="subscription-status">
                                  {subscription.status}
                                </span>
                              </div>
                              <p className="body-small" style={{ color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                                {subscription.email}
                              </p>
                              <p className="body-small" style={{ color: 'var(--text-muted)' }}>
                                R333/month • Paystack
                              </p>
                              {subscription.nextPaymentDate && (
                                <p className="body-small" style={{ color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                                  Next billing: {new Date(subscription.nextPaymentDate).toLocaleDateString()}
                                </p>
                              )}
                            </div>

                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <Button
                                onClick={() => {
                                  setSubscription(null);
                                  setManageEmail('');
                                }}
                                className="btn-secondary"
                                style={{ flex: 1 }}
                              >
                                Back
                              </Button>
                              {subscription.status !== 'cancelled' && (
                                <Button
                                  onClick={cancelSubscription}
                                  disabled={isCancelling}
                                  data-testid="cancel-subscription-btn"
                                  style={{
                                    flex: 1,
                                    background: 'rgba(239, 68, 68, 0.1)',
                                    color: '#dc2626',
                                    border: '1px solid rgba(239, 68, 68, 0.3)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.5rem'
                                  }}
                                >
                                  {isCancelling ? (
                                    <Loader2 size={16} className="animate-spin" />
                                  ) : (
                                    <XCircle size={16} />
                                  )}
                                  Cancel Subscription
                                </Button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <div className="membership-info">
                <h3 className="heading-2" style={{ marginBottom: '1.5rem' }}>Your Journey with BASADING WOMB & RELATIONSHIPS ARCADEMY™</h3>
                <div className="membership-image" style={{ marginBottom: '1.5rem', borderRadius: '0.75rem', overflow: 'hidden' }}>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_aec52aac-54e5-4016-8334-de13af7543de/artifacts/ez3syowj_bd108f84-66ca-4613-baf2-7a1beca38e54.jpeg" 
                    alt="BASADING WOMB & RELATIONSHIPS ARCADEMY™ Community Gathering"
                    style={{ width: '100%', height: 'auto', display: 'block' }}
                  />
                </div>
                <p className="body-medium" style={{ marginBottom: '1.5rem' }}>
                  BASADING WOMB & RELATIONSHIPS ARCADEMY™ is more than a membership—it's a sacred container for your healing and transformation. As a member, you become part of a supportive community of women committed to embodiment, pleasure as medicine, and soulful living.
                </p>
                <p className="body-medium" style={{ marginBottom: '1.5rem' }}>
                  Each month includes curated content, live healing experiences, and access to our private community where you can connect with like-minded souls on the same journey.
                </p>
                <p className="body-medium">
                  Your membership serves as a gentle funnel into deeper work—exclusive access to trainings, workshops, and transformative events designed to support your continued evolution.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Courses Section */}
      <section className="courses-section">
        <div className="container">
          <div className="section-header">
            <BookOpen size={32} style={{ color: 'var(--accent-gold)', marginBottom: '1rem' }} />
            <h2 className="heading-1">Upcoming Courses</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0.5rem auto 0' }}>
              Transformative programs currently in development for 2026
            </p>
          </div>

          <div className="courses-grid">
            {upcomingCourses.map((course, index) => (
              <Card key={index} className="course-card">
                <CardHeader>
                  <div className="course-status-badge">{course.status}</div>
                  <CardTitle>{course.title}</CardTitle>
                  <CardDescription>{course.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="body-small" style={{ color: 'var(--text-muted)' }}>
                    Platform: {course.platform}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Events Section */}
      <section className="events-section">
        <div className="container">
          <div className="section-header">
            <Calendar size={32} style={{ color: 'var(--accent-gold)', marginBottom: '1rem' }} />
            <h2 className="heading-1">Events & Workshops</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0.5rem auto 0' }}>
              Upcoming events and transformative gatherings
            </p>
          </div>

          {/* Book Your Spot CTA */}
          <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
              {!showBookingForm ? (
                <>
                <button
                  onClick={() => setShowBookingForm(true)}
                  className="btn-primary"
                  data-testid="book-spot-btn"
                  style={{
                    padding: '1rem 3rem',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    letterSpacing: '0.03em',
                    cursor: 'pointer'
                  }}
                >
                  Book Your Spot — R2,000 Deposit
                </button>
                <div style={{ marginTop: '1rem' }}>
                  <a
                    href="https://wa.me/27828983507?text=Hi%2C%20I%27d%20like%20to%20enquire%20about%20the%20upcoming%20event"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="whatsapp-enquiry-link"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      color: 'var(--accent-gold, #8B6914)',
                      textDecoration: 'none',
                      fontSize: '0.95rem',
                      fontWeight: '500'
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 00.611.611l4.458-1.495A11.943 11.943 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.387 0-4.591-.826-6.334-2.209l-.443-.357-3.1 1.04 1.04-3.1-.357-.443A9.935 9.935 0 012 12C2 6.486 6.486 2 12 2s10 4.486 10 10-4.486 10-10 10z"/></svg>
                    Enquire via WhatsApp
                  </a>
                </div>
              </>
              ) : (
                <div style={{
                  maxWidth: '450px',
                  margin: '0 auto',
                  padding: '2rem',
                  background: 'var(--bg-card, #fff)',
                  borderRadius: '1rem',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                }}>
                  <h4 className="heading-3" style={{ marginBottom: '1rem', textAlign: 'center' }}>
                    Secure Your Spot
                  </h4>
                  <p className="body-small" style={{ marginBottom: '1.5rem', textAlign: 'center', opacity: 0.7 }}>
                    R2,000 deposit to reserve your place
                  </p>
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    setBookingLoading(true);
                    setBookingError('');
                    try {
                      const response = await axios.post(`${API_URL}/api/bookings/initialize`, {
                        email: bookingData.email,
                        firstName: bookingData.firstName,
                        lastName: bookingData.lastName,
                        phoneNumber: bookingData.phoneNumber
                      });
                      if (response.data.success && response.data.authorization_url) {
                        window.location.href = response.data.authorization_url;
                      } else {
                        setBookingError('Failed to initialize payment. Please try again.');
                      }
                    } catch (err) {
                      setBookingError(err.response?.data?.detail || 'Something went wrong. Please try again.');
                    } finally {
                      setBookingLoading(false);
                    }
                  }} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <input
                      type="email"
                      placeholder="Email address"
                      required
                      value={bookingData.email}
                      onChange={(e) => setBookingData({...bookingData, email: e.target.value})}
                      style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem', border: '1px solid #ddd', fontSize: '0.95rem' }}
                      data-testid="booking-email"
                    />
                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                      <input
                        type="text"
                        placeholder="First name"
                        required
                        value={bookingData.firstName}
                        onChange={(e) => setBookingData({...bookingData, firstName: e.target.value})}
                        style={{ flex: 1, padding: '0.75rem 1rem', borderRadius: '0.5rem', border: '1px solid #ddd', fontSize: '0.95rem' }}
                        data-testid="booking-firstname"
                      />
                      <input
                        type="text"
                        placeholder="Last name"
                        required
                        value={bookingData.lastName}
                        onChange={(e) => setBookingData({...bookingData, lastName: e.target.value})}
                        style={{ flex: 1, padding: '0.75rem 1rem', borderRadius: '0.5rem', border: '1px solid #ddd', fontSize: '0.95rem' }}
                        data-testid="booking-lastname"
                      />
                    </div>
                    <input
                      type="tel"
                      placeholder="Phone number (optional)"
                      value={bookingData.phoneNumber}
                      onChange={(e) => setBookingData({...bookingData, phoneNumber: e.target.value})}
                      style={{ padding: '0.75rem 1rem', borderRadius: '0.5rem', border: '1px solid #ddd', fontSize: '0.95rem' }}
                      data-testid="booking-phone"
                    />
                    {bookingError && (
                      <div ref={errorRef} style={{ color: '#dc2626', fontSize: '0.875rem', textAlign: 'center', padding: '0.5rem' }}>
                        {bookingError}
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={bookingLoading}
                      className="btn-primary"
                      data-testid="booking-pay-btn"
                      style={{ padding: '0.85rem', fontSize: '1rem', fontWeight: '600', cursor: bookingLoading ? 'wait' : 'pointer' }}
                    >
                      {bookingLoading ? 'Processing...' : 'Pay R2,000 Deposit'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowBookingForm(false); setBookingError(''); }}
                      style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.875rem', padding: '0.5rem' }}
                    >
                      Cancel
                    </button>
                  </form>
                </div>
              )}
            </div>
        </div>
      </section>

      {/* External Platforms Section */}
      <section className="external-platforms-section">
        <div className="container">
          <h2 className="heading-1" style={{ textAlign: 'center', marginBottom: '3rem' }}>Course Platforms</h2>
          <div className="platforms-grid">
            <Card className="platform-card" style={{ position: 'relative', opacity: 0.85 }}>
              <div style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: '#1a1a1a',
                color: '#ffffff',
                padding: '0.4rem 1rem',
                borderRadius: '2rem',
                fontSize: '0.8rem',
                fontWeight: '600',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                zIndex: 1
              }}>Coming Soon</div>
              <CardHeader>
                <CardTitle>Sophia Grace Mystery School</CardTitle>
                <CardDescription>Thinkific Platform</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="body-medium" style={{ marginBottom: '1.5rem' }}>
                  Explore spiritual teachings and mystery school wisdom.
                </p>
              </CardContent>
            </Card>

            <Card className="platform-card" style={{ position: 'relative', opacity: 0.85 }}>
              <div style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: '#1a1a1a',
                color: '#ffffff',
                padding: '0.4rem 1rem',
                borderRadius: '2rem',
                fontSize: '0.8rem',
                fontWeight: '600',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                zIndex: 1
              }}>Coming Soon</div>
              <CardHeader>
                <CardTitle>Beautiful Vibes Womb Healing</CardTitle>
                <CardDescription>Teachable Platform</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="body-medium" style={{ marginBottom: '1.5rem' }}>
                  Sacred womb healing and feminine embodiment courses.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="courses-cta-section">
        <div className="container">
          <div className="courses-cta-content">
            <h2 className="heading-1" style={{ marginBottom: '1.5rem' }}>Ready to Begin Your Journey?</h2>
            <p className="body-large" style={{ marginBottom: '2rem' }}>
              Join BASADING WOMB & RELATIONSHIPS ARCADEMY™ today and become part of our healing community
            </p>
            <Button 
              className="btn-primary" 
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setTimeout(() => setShowForm(true), 500);
              }}
            >
              Subscribe to BASADING WOMB & RELATIONSHIPS ARCADEMY™ - R333/month
            </Button>
          </div>
        </div>
      </section>

      {/* Lightbox Modal for Event Posters */}
      {lightboxOpen && lightboxImage && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'rgba(255,255,255,0.9)',
              border: 'none',
              borderRadius: '50%',
              width: '44px',
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#333'
            }}
            aria-label="Close lightbox"
          >
            ✕
          </button>

          {/* Navigation Arrows */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              const currentIndex = eventPosters.findIndex(p => p.id === lightboxImage.id);
              const prevIndex = (currentIndex - 1 + eventPosters.length) % eventPosters.length;
              setLightboxImage(eventPosters[prevIndex]);
            }}
            style={{
              position: 'absolute',
              left: '20px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.9)',
              border: 'none',
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
            }}
            aria-label="Previous image"
          >
            <ChevronLeft size={28} color="#333" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              const currentIndex = eventPosters.findIndex(p => p.id === lightboxImage.id);
              const nextIndex = (currentIndex + 1) % eventPosters.length;
              setLightboxImage(eventPosters[nextIndex]);
            }}
            style={{
              position: 'absolute',
              right: '20px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(255,255,255,0.9)',
              border: 'none',
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
            }}
            aria-label="Next image"
          >
            <ChevronRight size={28} color="#333" />
          </button>

          {/* Image */}
          <img
            src={lightboxImage.src}
            alt={lightboxImage.alt}
            style={{
              maxWidth: '90%',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          />

          {/* Image Counter */}
          <div
            style={{
              position: 'absolute',
              bottom: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              color: 'white',
              fontSize: '14px',
              background: 'rgba(0,0,0,0.5)',
              padding: '8px 16px',
              borderRadius: '20px'
            }}
          >
            {eventPosters.findIndex(p => p.id === lightboxImage.id) + 1} / {eventPosters.length}
          </div>
        </div>
      )}
    </div>
  );
};
